/**
 * Single source of truth for the PAI loading screen.
 *
 * Consumed by two separate rendering paths:
 *  1. The Vite `transformIndexHtml` plugin — injects {@link LOADING_CSS} into
 *     `<head>` and {@link LOADING_HTML} into `<div id="root">` so the screen
 *     shows immediately, before the JS bundle finishes downloading.
 *  2. {@link PaiLoadingPage} — the React global-loading overlay rendered
 *     whenever `LoadingContext` is active (auth init, route changes, etc.).
 *
 * Plain CSS strings (no styled-components) so identical content renders in
 * both a static HTML context and inside React. Edit here; both consumers
 * update automatically.
 */

import {
  PI_BAR_PATH as BAR_PATH,
  PI_CARDINAL,
  PI_DASH as DASH,
  PI_FILL,
  PI_LEFT_PATH as LEFT_PATH,
  PI_RIGHT_PATH as RIGHT_PATH,
} from './pi/piPaths';

// ── Design tokens ──────────────────────────────────────────────────────────────

const C = PI_CARDINAL; // Stanford cardinal

// ── CSS ────────────────────────────────────────────────────────────────────────

/**
 * Full stylesheet for the PAI loading screen. Injected into `<head>` by the
 * Vite plugin; available globally for both the static HTML and React renders.
 *
 * Animation cycle: 4 s loop.
 *   0–5 %   : start pause (pen hovers)
 *   5–28 %  : bar draws left → right arch (~0.9 s)
 *   28–53 % : left leg draws junction → foot (~1.0 s)
 *   53–75 % : right leg draws junction → foot curl (~0.9 s)
 *   75–82 % : hold — all three strokes visible (0.3 s)
 *   82–100% : dark pause before loop restart (0.7 s)
 */
export const LOADING_CSS = `
/* ── Wrapper ──────────────────────────────────────────────── */

.pai-loading {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  z-index: 100;
}

/* ── π SVG ────────────────────────────────────────────────── */

/* viewBox 0 0 194 172 → preserve aspect ratio at ~170px wide */
.pai-l-spinner {
  width: 170px;
  height: 151px;
}

/* faint ghost keeps the target shape legible during animation */
.pai-l-ghost {
  fill: ${C};
  opacity: 0.08;
}

/* shared stroke style */
.pai-l-stroke {
  fill: none;
  stroke: ${C};
  stroke-linecap: round;
  stroke-dasharray: ${String(DASH)};
}

.pai-l-s-bar   { stroke-width: 24; animation: pai-l-bar   4s ease-in-out infinite; }
.pai-l-s-left  { stroke-width: 24; animation: pai-l-left  4s ease-in-out infinite; }
.pai-l-s-right { stroke-width: 24; animation: pai-l-right 4s ease-in-out infinite; }

/* ── Keyframes ────────────────────────────────────────────── */

@keyframes pai-l-bar {
  0%   { stroke-dashoffset: ${String(DASH)}; }
  5%   { stroke-dashoffset: ${String(DASH)}; animation-timing-function: cubic-bezier(.25,0,.4,1); }
  28%  { stroke-dashoffset: 0; }
  82%  { stroke-dashoffset: 0; animation-timing-function: steps(1, start); }
  100% { stroke-dashoffset: ${String(DASH)}; }
}

@keyframes pai-l-left {
  0%   { stroke-dashoffset: ${String(DASH)}; }
  28%  { stroke-dashoffset: ${String(DASH)}; animation-timing-function: cubic-bezier(.25,0,.4,1); }
  53%  { stroke-dashoffset: 0; }
  82%  { stroke-dashoffset: 0; animation-timing-function: steps(1, start); }
  100% { stroke-dashoffset: ${String(DASH)}; }
}

@keyframes pai-l-right {
  0%   { stroke-dashoffset: ${String(DASH)}; }
  53%  { stroke-dashoffset: ${String(DASH)}; animation-timing-function: cubic-bezier(.25,0,.4,1); }
  75%  { stroke-dashoffset: 0; }
  82%  { stroke-dashoffset: 0; animation-timing-function: steps(1, start); }
  100% { stroke-dashoffset: ${String(DASH)}; }
}
`;

// ── HTML ───────────────────────────────────────────────────────────────────────

/**
 * Full HTML fragment for the PAI loading screen. Injected into
 * `<div id="root">` by the Vite plugin (pre-React) and rendered via
 * `dangerouslySetInnerHTML` by {@link PaiLoadingPage} (React overlay).
 */
export const LOADING_HTML =
  `<div class="pai-loading">` +
  `<svg class="pai-l-spinner" viewBox="0 0 194 172" role="img" aria-label="π">` +
  `<path class="pai-l-ghost"  d="${PI_FILL}" />` +
  `<path class="pai-l-stroke pai-l-s-bar"   d="${BAR_PATH}" />` +
  `<path class="pai-l-stroke pai-l-s-left"  d="${LEFT_PATH}" />` +
  `<path class="pai-l-stroke pai-l-s-right" d="${RIGHT_PATH}" />` +
  `</svg>` +
  `</div>`;
